import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import {
  Alert,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
} from '@mui/material'

import { WrappedCard as RegistrationCard } from '../registration/RegistrationCard'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import GoogleLoginButton from './GoogleLoginButton'
import MicrosoftLoginButton from './MicrosoftLoginButton'

const errorText = {
  user_not_found:
    'A user with that email does not exist. Make sure you are signing in with the correct email.',
  non_oauth_account:
    'The account for this email was setup with a password. Please enter your password.',
  account_exists:
    'There is already an account associated your email address. Please log in.',
}

const ErrorWatcher = ({ error }) => {
  if (!error) return null

  return (
    <Alert sx={{ mb: 2 }} severity='error'>
      {errorText[error]}
    </Alert>
  )
}

const Login = () => {
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')
  const existingEmail = searchParams.get('email')

  const isExistingAccount = error === 'non_oauth_account' && !!existingEmail

  const [emailAddress, setEmailAddress] = useState(
    isExistingAccount ? existingEmail : ''
  )
  const [password, setPassword] = useState('')

  const navigate = useNavigate()

  const [submitError, setSubmitError] = useState(false)
  const [limitError, setLimitError] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const clearErrors = () => {
    setNotFound(false)
    setSubmitError(false)
    setLimitError(false)
  }

  const handleLogin = () => {
    clearErrors()
    axios
      .post('/api/auth/password', {
        emailAddress,
        password,
      })
      .then(() => {
        navigate('/dashboard')
      })
      .catch((error) => {
        if (error.response.status === 401) return setNotFound(true)
        if (error.response.status === 429) return setLimitError(true)

        setSubmitError(true)
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <RegistrationCard title={'Welcome back!'} sx={{ width: '500px' }}>
      <ErrorWatcher error={error} />
      <form>
        {!isExistingAccount && (
          <TextField
            autoFocus
            fullWidth
            margin={'dense'}
            value={emailAddress}
            label={'Email Address'}
            onChange={(e) => {
              clearErrors()
              setEmailAddress(e.target.value)
            }}
          />
        )}
        {isExistingAccount && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ textAlign: 'left', ml: 0.5 }}>
              Email address: <b>{emailAddress}</b>
            </Typography>
            <Button onClick={() => window.location.replace('/login')}>
              Not you?
            </Button>
          </Box>
        )}

        <TextField
          fullWidth
          margin={'dense'}
          value={password}
          type={showPassword ? 'text' : 'password'}
          label='Password'
          onChange={(e) => {
            clearErrors()
            setPassword(e.target.value)
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='toggle password visibility'
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {submitError && (
          <Alert severity='error' sx={{ mt: 1 }}>
            An error occured trying to log in. Please try again later
          </Alert>
        )}

        {notFound && (
          <Alert severity='error' sx={{ mt: 1 }}>
            The given email address and password combination did not match our
            records
          </Alert>
        )}

        {limitError && (
          <Alert severity='error' sx={{ mt: 1 }}>
            Too many failed attempts. Please try again later
          </Alert>
        )}

        <Box
          sx={{
            margin: '8px 0',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button type='button' onClick={() => navigate('/forgot')}>
            Forgot Password?
          </Button>
          <Button
            type='submit'
            variant={'contained'}
            onClick={(e) => {
              e.preventDefault()
              if (!emailAddress || !password) return

              setSubmitting(true)
              // allow browser to fill in password before submitting
              setTimeout(() => {
                handleLogin()
              }, 50)
            }}
            disabled={limitError || submitting}
          >
            Login
          </Button>
        </Box>
      </form>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          my: 1,
          alignItems: 'center',
        }}
      >
        <GoogleLoginButton />
        <MicrosoftLoginButton />
      </Box>

      <div className='flex justify-content-center mt-16'>
        <Button
          type='button'
          variant={'outlined'}
          onClick={() => navigate('/register')}
        >
          Sign Up
        </Button>
      </div>
    </RegistrationCard>
  )
}

export default Login
